import React from 'react'
import PropTypes from 'prop-types'
import { Table, Tag, Select, Card, Tooltip, message } from 'antd'
import { CheckCircleOutlined, ClockCircleOutlined, EyeOutlined, DownloadOutlined, FileTextOutlined, FilePdfOutlined } from '@ant-design/icons'
import moment from 'moment'
import { downloadRamsSignOffSheet, downloadRamsRevisionSheet, downloadRamsFullWithSignOff } from '../../../../HttpRequests/ramsControlCentre'
import './Component.css'

var Option = Select.Option

export default class _ActiveRamsTable_ extends React.PureComponent {

  static propTypes = {
    data: PropTypes.array.isRequired,
    fetching: PropTypes.bool.isRequired,
    pagination: PropTypes.object.isRequired,
    projectsList: PropTypes.array.isRequired,
    onChangeTable: PropTypes.func.isRequired,
    onProjectFilterChange: PropTypes.func.isRequired,
    onStatusFilterChange: PropTypes.func.isRequired,
    onClickRecord: PropTypes.func.isRequired,
  }

  state = {
    selectedProjectId: null,
    selectedStatus: [],
    downloadingSignOff: {},
    downloadingRevision: {},
    downloadingFull: {},
  }

  handleProjectChange = (value) => {
    this.setState({ selectedProjectId: value || null })
    this.props.onProjectFilterChange(value || null)
  }

  handleStatusChange = (value) => {
    this.setState({ selectedStatus: value || [] })
    this.props.onStatusFilterChange(value || [])
  }

  handleDownloadSignOff = async (ramsRecordId) => {
    this.setState(prev => ({ downloadingSignOff: { ...prev.downloadingSignOff, [ramsRecordId]: true } }))
    try {
      const response = await downloadRamsSignOffSheet(ramsRecordId)
      if (response.data && response.data.download_url) {
        window.open(response.data.download_url, '_blank')
      }
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : 'Sign-off sheet not yet available'
      message.error(msg)
    } finally {
      this.setState(prev => ({ downloadingSignOff: { ...prev.downloadingSignOff, [ramsRecordId]: false } }))
    }
  }

  handleDownloadRevision = async (ramsRecordId) => {
    this.setState(prev => ({ downloadingRevision: { ...prev.downloadingRevision, [ramsRecordId]: true } }))
    try {
      const response = await downloadRamsRevisionSheet(ramsRecordId)
      var blob = new Blob([response.data], { type: 'application/pdf' })
      var url = window.URL.createObjectURL(blob)
      var link = document.createElement('a')
      link.href = url
      link.download = 'RAMS_Revisions_' + ramsRecordId + '_' + moment().format('YYYY-MM-DD') + '.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : 'Could not generate revision sheet'
      message.error(msg)
    } finally {
      this.setState(prev => ({ downloadingRevision: { ...prev.downloadingRevision, [ramsRecordId]: false } }))
    }
  }

  handleDownloadFull = async (ramsRecordId) => {
    this.setState(prev => ({ downloadingFull: { ...prev.downloadingFull, [ramsRecordId]: true } }))
    try {
      const response = await downloadRamsFullWithSignOff(ramsRecordId)
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `RAMS_Full_SignOff_${ramsRecordId}_${moment().format('YYYY-MM-DD')}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : 'Could not generate combined PDF'
      message.error(msg)
    } finally {
      this.setState(prev => ({ downloadingFull: { ...prev.downloadingFull, [ramsRecordId]: false } }))
    }
  }

  getStatusTag = (status) => {
    if (status === 'approved') return <Tag color="success">Approved</Tag>
    if (status === 'awaiting_approval') return <Tag color="processing">Awaiting Approval</Tag>
    if (status === 'expired') return <Tag color="error">Expired</Tag>
    return <Tag color="default">Unapproved</Tag>
  }

  getColumns = () => {
    var { downloadingSignOff, downloadingRevision, downloadingFull } = this.state
    var self = this
    return [
      {
        title: 'No.',
        key: 'id',
        sorter: false,
        width: 100,
        render: function(text, record) {
          return (
            <a role="presentation">
              {'RAMS ' + record.id}
            </a>
          )
        },
      },
      {
        title: 'Project',
        key: 'project',
        sorter: false,
        render: function(text, record) {
          if (!record.project) return '-'
          return record.project.title + ' (' + record.project.number + ')'
        },
      },
      {
        title: 'Status',
        key: 'status',
        width: 130,
        sorter: false,
        render: function(text, record) {
          return self.getStatusTag(record.status || 'unapproved')
        },
      },
      {
        title: 'Signed',
        key: 'signed_count',
        width: 100,
        sorter: false,
        render: function(text, record) {
          if (record.total === 0) return <Tag>0 / 0</Tag>
          var color = record.total_signed === record.total ? 'success' : 'processing'
          return <Tag color={color}>{record.total_signed} / {record.total}</Tag>
        },
      },
      {
        title: 'Valid From',
        key: 'valid_from',
        width: 150,
        sorter: false,
        render: function(text, record) {
          return record.valid_from ? moment(record.valid_from).format('DD MMM YYYY') : ''
        },
      },
      {
        title: 'Valid To',
        key: 'valid_to',
        width: 150,
        sorter: false,
        render: function(text, record) {
          return record.valid_to ? moment(record.valid_to).format('DD MMM YYYY') : ''
        },
      },
      {
        title: 'Sign-off',
        key: 'signoff_sheet',
        width: 80,
        sorter: false,
        render: function(text, record) {
          var loading = downloadingSignOff[record.id]
          return (
            <Tooltip title={loading ? 'Downloading...' : 'Download sign-off sheet'}>
              <FileTextOutlined
                style={{ fontSize: 18, color: loading ? '#ccc' : '#52c41a', cursor: loading ? 'wait' : 'pointer' }}
                onClick={loading ? null : function(e) { e.stopPropagation(); self.handleDownloadSignOff(record.id) }}
              />
            </Tooltip>
          )
        },
      },
      {
        title: 'Revision',
        key: 'revision_sheet',
        width: 80,
        sorter: false,
        render: function(text, record) {
          var loading = downloadingRevision[record.id]
          return (
            <Tooltip title={loading ? 'Generating...' : 'Download revision sheet'}>
              <FileTextOutlined
                style={{ fontSize: 18, color: loading ? '#ccc' : '#fa8c16', cursor: loading ? 'wait' : 'pointer' }}
                onClick={loading ? null : function(e) { e.stopPropagation(); self.handleDownloadRevision(record.id) }}
              />
            </Tooltip>
          )
        },
      },
      {
        title: 'Full Doc',
        key: 'full_document',
        width: 80,
        sorter: false,
        render: function(text, record) {
          var loading = downloadingFull[record.id]
          return (
            <Tooltip title={loading ? 'Generating...' : 'Download full document (+sign off +revision)'}>
              <FilePdfOutlined
                style={{ fontSize: 18, color: loading ? '#ccc' : '#722ed1', cursor: loading ? 'wait' : 'pointer' }}
                onClick={loading ? null : function(e) { e.stopPropagation(); self.handleDownloadFull(record.id) }}
              />
            </Tooltip>
          )
        },
      },
    ]
  }

  getSignedColumns = () => {
    return [
      {
        title: 'Subcontractor',
        dataIndex: 'subcontractor_name',
        key: 'subcontractor_name',
      },
      {
        title: 'Status',
        key: 'signed_status',
        width: 120,
        render: function(text, record) {
          if (record.signed) {
            return <Tag icon={<CheckCircleOutlined />} color="success">Signed</Tag>
          }
          if (record.viewed) {
            return <Tag icon={<EyeOutlined />} color="processing">Viewed</Tag>
          }
          return <Tag icon={<ClockCircleOutlined />} color="warning">Pending</Tag>
        },
      },
      {
        title: 'Sent',
        dataIndex: 'sent_at',
        key: 'sent_at',
        render: function(text) {
          return text ? moment(text).format('DD/MM/YYYY HH:mm') : '-'
        },
      },
      {
        title: 'Signed At',
        dataIndex: 'signed_at',
        key: 'signed_at',
        render: function(text) {
          return text ? moment(text).format('DD/MM/YYYY HH:mm') : '-'
        },
      },
      {
        title: 'Signed PDF',
        key: 'download',
        width: 80,
        render: function(text, record) {
          if (!record.signed) return null
          if (record.signed_pdf_url) {
            return (
              <a href={record.signed_pdf_url} target="_blank" rel="noopener noreferrer" title="Download signed PDF">
                <DownloadOutlined style={{ fontSize: 18, color: '#1890FF' }} />
              </a>
            )
          }
          return null
        },
      },
    ]
  }

  renderExpandedRow = (record) => {
    var signedRecords = record.signed_records || []
    if (signedRecords.length === 0) {
      return <p style={{ margin: 0, color: '#999', fontStyle: 'italic' }}>No signed records for this RAMS.</p>
    }
    return (
      <Table
        rowKey="id"
        columns={this.getSignedColumns()}
        dataSource={signedRecords}
        pagination={false}
        size="small"
      />
    )
  }

  render () {

    var self = this
    var { data, fetching, pagination, projectsList } = this.props
    var { selectedProjectId, selectedStatus } = this.state

    var tablePagination = {
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: pagination.total,
      showSizeChanger: true,
      showTotal: function(total) { return 'Total: ' + total + ' records' },
    }

    const scroll = { x: this.getColumns().reduce((total, column) => total + (column.width || 150), 0) };

    return (
      <div className="rams-table-wrapper">
        <Card
          title="RAMS Records"
          extra={
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Select
                mode="multiple"
                placeholder="Filter by Status"
                style={{ minWidth: 200 }}
                allowClear
                value={selectedStatus}
                onChange={this.handleStatusChange}
              >
                <Option value="unapproved">Unapproved</Option>
                <Option value="awaiting_approval">Awaiting Approval</Option>
                <Option value="approved">Approved</Option>
                <Option value="expired">Expired</Option>
              </Select>
              <Select
                placeholder="Filter by Project"
                style={{ width: 300 }}
                allowClear
                showSearch
                optionFilterProp="children"
                value={selectedProjectId}
                onChange={this.handleProjectChange}
              >
                {(projectsList || []).map(function(project) {
                  return (
                    <Option key={project.id} value={project.id}>
                      {project.title}
                    </Option>
                  )
                })}
              </Select>
            </div>
          }
        >
          <Table
            rowKey="id"
            columns={this.getColumns()}
            dataSource={data}
            loading={fetching}
            pagination={tablePagination}
            onChange={this.props.onChangeTable}
            size="small"
            scroll={scroll}
            expandable={{
              expandedRowRender: this.renderExpandedRow,
              rowExpandable: function() { return true },
            }}
            onRow={function(record) {
              return {
                onClick: function() { self.props.onClickRecord(record) },
                style: { cursor: 'pointer' },
              }
            }}
          />
        </Card>
      </div>
    )
  }
}
